import { useDrawingBoard } from './useDrawingBoard';

export type DrawingBoardState = ReturnType<typeof useDrawingBoard>;

export default function LayerPanel({ board }: { board: DrawingBoardState }) {
  const {
    layers, activeLayerId, setActiveLayerId, addLayer, toggleLayerVisibility, toggleLayerLock,
    renamingLayerId, setRenamingLayerId, renameValue, setRenameValue, confirmRename, startRename,
    moveLayer, deleteLayer, getActiveLayer, isFullscreen
  } = board;

  return (
            <div className={`flex-shrink-0 rounded-lg border p-3 space-y-3 ${
              isFullscreen
                ? 'w-60 bg-stone-800 border-stone-700 overflow-y-auto'
                : 'w-full lg:w-56 bg-stone-50 border-stone-200'
            }`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-semibold ${isFullscreen ? 'text-stone-300' : 'text-stone-700'}`}>Katmanlar</h3>
                <button
                  onClick={addLayer}
                  className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                    isFullscreen
                      ? 'bg-stone-700 text-stone-400 hover:bg-stone-600 hover:text-white'
                      : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                  }`}
                  title="Yeni Katman"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>

              <div className="space-y-1">
                {[...layers].reverse().map((layer, reversedIndex) => {
                  const actualIndex = layers.length - 1 - reversedIndex;
                  const isActive = layer.id === activeLayerId;
                  const isLocked = layer.locked;

                  return (
                    <div
                      key={layer.id}
                      onClick={() => !isLocked && setActiveLayerId(layer.id)}
                      className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        isActive
                          ? (isFullscreen ? 'bg-stone-700 border border-stone-600' : 'bg-amber-100 border border-amber-200')
                          : (isFullscreen ? 'hover:bg-stone-700/50 border border-transparent' : 'hover:bg-stone-100 border border-transparent')
                      } ${isLocked ? 'opacity-60' : ''}`}
                    >
                      {/* Visibility Toggle */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
                        className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                          isFullscreen ? 'text-stone-400 hover:text-stone-200' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        title={layer.visible ? 'Gizle' : 'Göster'}
                      >
                        <i className={layer.visible ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                      </button>

                      {/* Lock Toggle */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLayerLock(layer.id); }}
                        className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                          isFullscreen ? 'text-stone-400 hover:text-stone-200' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        title={layer.locked ? 'Kilidi Aç' : 'Kilitle'}
                      >
                        <i className={layer.locked ? 'ri-lock-line' : 'ri-lock-unlock-line'}></i>
                      </button>

                      {/* Layer Name */}
                      <div className="flex-1 min-w-0">
                        {renamingLayerId === layer.id ? (
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={confirmRename}
                            onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenamingLayerId(null); }}
                            autoFocus
                            className={`w-full px-1 py-0.5 text-sm rounded focus:outline-none ${
                              isFullscreen
                                ? 'bg-stone-700 border border-amber-500 text-white'
                                : 'bg-white border border-amber-400'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span
                            onDoubleClick={(e) => { e.stopPropagation(); startRename(layer); }}
                            className={`truncate block ${isFullscreen ? 'text-stone-300' : 'text-stone-700'}`}
                            title={layer.name}
                          >
                            {layer.name}
                          </span>
                        )}
                      </div>

                      {/* Move Controls */}
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 'up'); }}
                          disabled={actualIndex === layers.length - 1}
                          className={`w-5 h-5 flex items-center justify-center rounded transition-colors cursor-pointer disabled:opacity-30 ${
                            isFullscreen ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600'
                          }`}
                          title="Yukarı Taşı"
                        >
                          <i className="ri-arrow-up-s-line text-xs"></i>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 'down'); }}
                          disabled={actualIndex === 0}
                          className={`w-5 h-5 flex items-center justify-center rounded transition-colors cursor-pointer disabled:opacity-30 ${
                            isFullscreen ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600'
                          }`}
                          title="Aşağı Taşı"
                        >
                          <i className="ri-arrow-down-s-line text-xs"></i>
                        </button>
                      </div>

                      {/* Delete */}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }}
                        className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                          isFullscreen ? 'text-stone-500 hover:text-red-400' : 'text-stone-400 hover:text-red-500'
                        }`}
                        title="Katmanı Sil"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Active Layer Info */}
              {getActiveLayer() && (
                <div className={`pt-2 border-t ${isFullscreen ? 'border-stone-700' : 'border-stone-200'}`}>
                  <p className={`text-xs ${isFullscreen ? 'text-stone-500' : 'text-stone-500'}`}>
                    Aktif: <span className={`font-medium ${isFullscreen ? 'text-stone-300' : 'text-stone-700'}`}>{getActiveLayer()?.name}</span>
                  </p>
                  <p className={`text-xs mt-0.5 ${isFullscreen ? 'text-stone-600' : 'text-stone-400'}`}>
                    {getActiveLayer()?.paths.length || 0} çizim
                  </p>
                </div>
              )}
            </div>
  );
}
